import { useState } from 'react'

import { apiFetch } from '@/lib/api'
import type { Project } from '@/types/domain.types'

interface UseProjectActionsOptions {
  projects: Project[]
  setProjects: (projects: Project[]) => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export function useProjectActions({
  projects,
  setProjects,
  onSuccess,
  onError,
}: UseProjectActionsOptions) {
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean
    projectId?: string
    projectName?: string
  }>({ show: false })

  const [renameDialog, setRenameDialog] = useState<{
    show: boolean
    project?: Project
  }>({ show: false })

  const handleRename = (project: Project) => {
    setRenameDialog({ show: true, project })
  }

  const performRename = async (newName: string) => {
    const project = renameDialog.project
    if (!project) return

    try {
      const response = await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      })

      if (!response.ok) throw new Error('Failed to rename project')

      setProjects(projects.map((p) => (p.id === project.id ? { ...p, name: newName } : p)))
      setRenameDialog({ show: false })
      onSuccess('Project renamed successfully')
    } catch (err) {
      onError(`Rename failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
      throw err
    }
  }

  const handleCopyId = (project: Project) => {
    navigator.clipboard.writeText(project.id)
    onSuccess('Project ID copied to clipboard!')
  }

  const handleDelete = (project: Project) => {
    setConfirmDialog({ show: true, projectId: project.id, projectName: project.name })
  }

  const confirmDelete = async () => {
    if (!confirmDialog.projectId) return

    try {
      const response = await apiFetch(`/projects/${confirmDialog.projectId}`, {
        method: 'DELETE',
      })

      if (!response.ok) throw new Error('Failed to delete project')

      setProjects(projects.filter((p) => p.id !== confirmDialog.projectId))
      onSuccess('Project deleted successfully')
    } catch (err) {
      onError(`Delete failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }

    setConfirmDialog({ show: false })
  }

  const handleExport = async (project: Project) => {
    setProjects(
      projects.map((p) => (p.id === project.id ? { ...p, status: 'rendering' as const } : p))
    )

    try {
      const response = await apiFetch(`/projects/${project.id}/render`, { method: 'POST' })

      if (!response.ok) throw new Error('Failed to start render')

      // Success notification removed - cover processing notification is sufficient
    } catch (err) {
      setProjects(projects.map((p) => (p.id === project.id ? { ...p, status: 'draft' as const } : p)))
      onError(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleDownload = async (project: Project) => {
    try {
      const url = `https://mcp.getbalboa.com/api/projects/${project.id}/download`
      const a = document.createElement('a')
      a.href = url
      a.download = `${project.name || 'video'}.mp4`
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch (err) {
      onError('Download failed')
    }
  }

  return {
    handleRename,
    handleCopyId,
    handleDelete,
    handleExport,
    handleDownload,
    confirmDialog,
    setConfirmDialog,
    confirmDelete,
    renameDialog,
    setRenameDialog,
    performRename,
  }
}

